/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, breakpoints } from '@coursera/cds-core';

import BrowseCarousel from 'bundles/browse/components/BrowseCarousel';
import {
  COLLECTION_CAROUSEL_PRODUCT_CARD_IMAGE_HEIGHT,
  defaultResponsiveProperties,
} from 'bundles/collections-carousel/constants';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import ProductCardContainer from 'bundles/product-card/components/legacy/ProductCardContainer';

import _t from 'i18n!nls/collections-carousel';

const styles = {
  carouselContainer: () => css`
    width: 100%;
    max-width: ${breakpoints.values.lg}px;
    margin: 0 auto;
    padding: 60px 15px 60px 0;

    .rc-BrowseCarousel {
      height: 400px;

      /* Required to show nav arrows at all screen widths */
      @media screen and (max-width: 1560px) {
        padding: 0 50px;
      }

      @media screen and (max-width: 500px) {
        padding: 0;
      }

      .slick-list {
        padding-top: 10px;
      }
    }

    /* Required to override a specific breakpoint that causes the cards to get cut off at small screen sizes */
    .rc-BrowseCarousel .slick-slider {
      @media (max-width: 479px) {
        max-width: calc(100% - 88px);
      }

      @media (max-width: 375px) {
        max-width: 100%;
      }
    }
  `,
  titleLink: () => css`
    color: var(--cds-color-black-500);
  `,
  title: css`
    margin-bottom: 20px;
  `,
  productContainerStyles: css`
    /* enforce the same height and width of the old collection card: BrowseProductCard, so our new product cards can function properly in BrowseCarousel */
    height: 384px;
    width: 268px;
  `,
  truncatedName: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `,
  cardPadding: css`
    padding: 0 6px;
  `,
};

const getDataE2ETag = (collectionIdForTracking: string) => {
  switch (String(collectionIdForTracking)) {
    case 'degree':
      return 'degree-card-collection';
    case 'mastertrack':
      return 'mastertrack-card-collection';
    case 'university-certificate':
      return 'university-certificate-card-collection';
    default:
      return 'mixed-card-collection';
  }
};

const CollectionTitle: React.FC<{ title: string; url?: string | null }> = ({ title, url }) => {
  const collectionTitle = url ? (
    <TrackedLink2
      href={url}
      data={{ url }}
      trackingName="collection_carousel_title_link_to_collection_page"
      css={styles.titleLink}
    >
      {title}
    </TrackedLink2>
  ) : (
    title
  );

  return (
    <Typography variant="h1" component="h2" css={styles.title}>
      {collectionTitle}
    </Typography>
  );
};

const CollectionsCarousel: React.FC<{
  collection: CarouselCollection;
  collectionIndex: number;
  customResponsiveProperties?: {
    breakpoint: number;
    settings: { slidesToShow: number; slidesToScroll: number; infinite: boolean };
  }[];
}> = ({ collection, collectionIndex, customResponsiveProperties }) => {
  const { title, entities, collectionLink, header, footer, collectionTrackingData } = collection;
  if (!entities || entities.length === 0) {
    return null;
  }

  let trackingId = '';
  if (collectionTrackingData && collectionTrackingData.collectionId) {
    trackingId = collectionTrackingData.collectionId as string;
  }

  return (
    <section
      css={styles.carouselContainer}
      aria-label={_t('#{title} Carousel', { title })}
      data-e2e={getDataE2ETag(trackingId)}
    >
      {header ?? <CollectionTitle title={title} url={collectionLink} />}
      <BrowseCarousel
        responsive={
          customResponsiveProperties
            ? [...defaultResponsiveProperties, ...customResponsiveProperties]
            : defaultResponsiveProperties
        }
        slidesToShow={Math.min(4, entities.length)}
        slidesToScroll={Math.min(4, entities.length)}
        trackingData={collectionTrackingData}
        enableRtl
        isProductCardExperiment
      >
        {entities.filter(Boolean).map((entity, entityIndex) => (
          <div css={styles.cardPadding} key={entityIndex}>
            <ProductCardContainer
              entity={entity}
              entityIndex={entityIndex}
              trackingName="collection_product_card"
              trackingData={{ ...collection.collectionTrackingData, ...entity.trackingData }}
              customStyles={styles.productContainerStyles}
              cardHeader={
                <Typography variant="h2semibold" css={styles.truncatedName}>
                  {entity.name}
                </Typography>
              }
              productCardImageProps={{ height: COLLECTION_CAROUSEL_PRODUCT_CARD_IMAGE_HEIGHT }}
              truncatePartnerName
              carouselIndex={collectionIndex}
            />
          </div>
        ))}
      </BrowseCarousel>
      {footer}
    </section>
  );
};
export default CollectionsCarousel;
